import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  AlertTriangle,
  Cloud,
  CloudOff,
  FolderPlus,
  FolderSync,
  HardDrive,
  History,
  Loader2,
  Lock,
  Plus,
  RotateCcw,
  ShieldCheck,
  Trash2,
  Users,
} from "@fleet/icons";
import { Button } from "@/components/ui/button";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  Skeleton,
  Switch,
  TextField,
} from "@fleet/ui";
import { Select } from "@/components/ui/select";
import { api, createApiQuery, fetchApi } from "@/lib/api";
import { type components, queryKeys } from "@bodega/shared";
import { useI18n } from "@/providers/i18n-provider";
import {
  conflictSides,
  deriveSyncStatus,
  formatBytes,
  jobFileCount,
  partitionVersions,
  shortHash,
} from "../lib/status";
import {
  orgScopeOptions,
  PERSONAL_SCOPE,
  scopeToOrganizationId,
  teamBadgeLabel,
} from "../lib/scope";
import { TeamKeySection } from "./team-key-section";
import { QueryErrorState } from "@/components/ui/query-error-state";

type RemoteView = components["schemas"]["RemoteView"];
type FolderView = components["schemas"]["FolderView"];
type FileVersionView = components["schemas"]["FileVersionView"];
type SyncJobView = components["schemas"]["SyncJobView"];
type ErrorModel = components["schemas"]["ErrorModel"];

type RemoteKind = "s3" | "webdav" | "drive";

/**
 * BYOS control plane (Bodega P-1): connect the user's OWN storage (S3-compatible
 * or WebDAV; Google Drive OAuth is manual), review a folder's sync status, and
 * resolve conflicts / restore previous versions. File bytes move device↔remote
 * on the client datapath and never touch this server — this page only drives the
 * persistent domain (`/api/byos`).
 /** Waits for the app to finish booting (skeleton overlay disappears). */
export function ByosStorage() {
  const { t } = useI18n();

  const [selectedRemote, setSelectedRemote] = createSignal<string | null>(null);
  const [selectedFolder, setSelectedFolder] = createSignal<string | null>(null);

  const remotes = createApiQuery<RemoteView[] | null, ErrorModel>(
    () => ["byos", "remotes"],
    async () => api.GET("/api/byos/remotes", {}),
  );

  // Team folders (multi-org): the user's organizations feed the scope selector
  // of the connect form and the team badge of each shared remote (CC-40 — the
  // server only lists remotes of orgs with LIVE membership).
  const organizations = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    ErrorModel
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const folders = createApiQuery<FolderView[] | null, ErrorModel>(
    () => ["byos", "folders", selectedRemote()],
    async () =>
      api.GET("/api/byos/remotes/{id}/folders", {
        params: { path: { id: selectedRemote() ?? "" } },
      }),
    () => ({ enabled: Boolean(selectedRemote()) }),
  );

  const versions = createApiQuery<FileVersionView[] | null, ErrorModel>(
    () => ["byos", "versions", selectedFolder()],
    async () =>
      api.GET("/api/byos/folders/{id}/versions", {
        params: { path: { id: selectedFolder() ?? "" } },
      }),
    () => ({ enabled: Boolean(selectedFolder()) }),
  );

  const jobs = createApiQuery<SyncJobView[] | null, ErrorModel>(
    () => ["byos", "jobs", selectedFolder()],
    async () =>
      api.GET("/api/byos/folders/{id}/jobs", {
        params: { path: { id: selectedFolder() ?? "" } },
      }),
    () => ({ enabled: Boolean(selectedFolder()) }),
  );

  const partitioned = createMemo(() =>
    partitionVersions(versions.isSuccess ? (versions.data ?? []) : []),
  );
  const status = createMemo(() =>
    deriveSyncStatus(jobs.isSuccess ? (jobs.data ?? []) : [], partitioned().conflicts.length),
  );

  const refreshFolderPanels = () => {
    void versions.refetch();
    void jobs.refetch();
  };

  // ── Connect a remote ────────────────────────────────────────────────────────
  const [kind, setKind] = createSignal<RemoteKind>("s3");
  const [scope, setScope] = createSignal<string>(PERSONAL_SCOPE);
  const scopeOptions = createMemo(() => [
    { value: PERSONAL_SCOPE, label: t(msg`Personal`) },
    ...orgScopeOptions(organizations.isSuccess ? organizations.data : undefined),
  ]);
  const [name, setName] = createSignal("");
  const [bucket, setBucket] = createSignal("");
  const [region, setRegion] = createSignal("");
  const [endpoint, setEndpoint] = createSignal("");
  const [accessKey, setAccessKey] = createSignal("");
  const [secretKey, setSecretKey] = createSignal("");
  const [forcePathStyle, setForcePathStyle] = createSignal(true);
  const [baseUrl, setBaseUrl] = createSignal("");
  const [username, setUsername] = createSignal("");
  const [password, setPassword] = createSignal("");
  const [encrypted, setEncrypted] = createSignal(false);
  const [connecting, setConnecting] = createSignal(false);
  const [connectError, setConnectError] = createSignal<string | null>(null);

  const kindOptions = createMemo(() => [
    { value: "s3" as const, label: t(msg`S3 / R2 / B2 / MinIO`) },
    { value: "webdav" as const, label: t(msg`WebDAV / Nextcloud`) },
    { value: "drive" as const, label: t(msg`Google Drive (manual)`) },
  ]);

  const canConnect = () => {
    if (!name().trim() || connecting()) {
      return false;
    }
    if (kind() === "s3") {
      return Boolean(bucket().trim());
    }
    if (kind() === "webdav") {
      return Boolean(baseUrl().trim());
    }
    return true; // drive stub
  };

  const connect = async () => {
    if (!canConnect()) {
      return;
    }
    setConnecting(true);
    setConnectError(null);
    try {
      const config: components["schemas"]["RemoteConfig"] =
        kind() === "s3"
          ? {
              bucket: bucket().trim(),
              region: region().trim() || "us-east-1",
              access_key: accessKey().trim(),
              secret_key: secretKey().trim(),
              endpoint: endpoint().trim() || undefined,
              force_path_style: forcePathStyle(),
            }
          : kind() === "webdav"
            ? {
                base_url: baseUrl().trim(),
                username: username().trim(),
                password: password(),
              }
            : {};
      const view = await fetchApi(
        api.POST("/api/byos/remotes", {
          body: {
            name: name().trim(),
            kind: kind(),
            config,
            encrypted: encrypted(),
            organization_id: scopeToOrganizationId(scope()),
          },
        }),
      );
      setName("");
      setBucket("");
      setAccessKey("");
      setSecretKey("");
      setBaseUrl("");
      setUsername("");
      setPassword("");
      setEncrypted(false);
      await remotes.refetch();
      setSelectedRemote(view.id);
    } catch (err) {
      setConnectError(
        err instanceof Error && err.message
          ? err.message
          : t(msg`Could not connect. Check the address and credentials.`),
      );
    } finally {
      setConnecting(false);
    }
  };

  const disconnect = async (remoteId: string) => {
    await fetchApi(api.DELETE("/api/byos/remotes/{id}", { params: { path: { id: remoteId } } }));
    if (selectedRemote() === remoteId) {
      setSelectedRemote(null);
      setSelectedFolder(null);
    }
    void remotes.refetch();
  };

  // ── Add a folder ─────────────────────────────────────────────────────────────
  const [folderName, setFolderName] = createSignal("");
  const [remotePrefix, setRemotePrefix] = createSignal("");
  const [addingFolder, setAddingFolder] = createSignal(false);
  const addFolder = async () => {
    const rid = selectedRemote();
    if (!rid || !folderName().trim() || addingFolder()) {
      return;
    }
    setAddingFolder(true);
    try {
      const view = await fetchApi(
        api.POST("/api/byos/remotes/{id}/folders", {
          params: { path: { id: rid } },
          body: {
            name: folderName().trim(),
            local_prefix: "",
            remote_prefix: remotePrefix().trim(),
          },
        }),
      );
      setFolderName("");
      setRemotePrefix("");
      await folders.refetch();
      setSelectedFolder(view.id);
    } finally {
      setAddingFolder(false);
    }
  };

  // ── Conflicts + versions ─────────────────────────────────────────────────────
  const [busyVersion, setBusyVersion] = createSignal<string | null>(null);

  const resolve = async (versionId: string, keepCurrent: boolean) => {
    const fid = selectedFolder();
    if (!fid) {
      return;
    }
    setBusyVersion(versionId);
    try {
      await fetchApi(
        api.POST("/api/byos/folders/{id}/conflicts/{version_id}/resolve", {
          params: { path: { id: fid, version_id: versionId } },
          body: { keep_current: keepCurrent },
        }),
      );
      refreshFolderPanels();
    } finally {
      setBusyVersion(null);
    }
  };

  const restore = async (versionId: string) => {
    const fid = selectedFolder();
    if (!fid) {
      return;
    }
    setBusyVersion(versionId);
    try {
      await fetchApi(
        api.POST("/api/byos/folders/{id}/versions/{version_id}/restore", {
          params: { path: { id: fid, version_id: versionId } },
        }),
      );
      refreshFolderPanels();
    } finally {
      setBusyVersion(null);
    }
  };

  const healthBadge = () => {
    const s = status();
    switch (s.health) {
      case "healthy": {
        return { label: t(msg`In sync`), variant: "outline" as const, icon: ShieldCheck };
      }
      case "attention": {
        return {
          label: t(msg`Conflicts to resolve`),
          variant: "secondary" as const,
          icon: AlertTriangle,
        };
      }
      case "error": {
        return { label: t(msg`Last sync failed`), variant: "destructive" as const, icon: CloudOff };
      }
      default: {
        return { label: t(msg`Not synced yet`), variant: "outline" as const, icon: Cloud };
      }
    }
  };

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Your storage`)}</h2>
        <Show when={remotes.isRefetching || folders.isRefetching}>
          <Loader2 class="h-4 w-4 animate-spin text-muted-foreground" />
        </Show>
      </div>
      <p class="text-sm text-muted-foreground">
        {t(
          msg`Connect your OWN storage (S3, R2, Backblaze, WebDAV/Nextcloud) and Bodega syncs your folders directly to it. Your files never touch our servers.`,
        )}
      </p>

      <div class="grid gap-4 lg:grid-cols-2">
        {/* Connect a remote */}
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <HardDrive class="h-4 w-4 text-primary" />
              {t(msg`Connect a remote`)}
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-3">
            <div class="space-y-2">
              <Label class="text-xs font-semibold">{t(msg`Type`)}</Label>
              <Select<RemoteKind>
                options={kindOptions()}
                value={kind()}
                onChange={setKind}
                aria-label={t(msg`Remote type`)}
                triggerClass="w-full"
              />
            </div>
            {/* Team folders: only meaningful when the user belongs to an org */}
            <Show when={(organizations.data?.length ?? 0) > 0}>
              <div class="space-y-2" data-testid="byos-scope-field">
                <Label class="text-xs font-semibold">{t(msg`Scope`)}</Label>
                <Select<string>
                  options={scopeOptions()}
                  value={scope()}
                  onChange={setScope}
                  aria-label={t(msg`Remote scope`)}
                  triggerClass="w-full"
                />
                <Show when={scope() !== PERSONAL_SCOPE}>
                  <p class="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Users class="h-3.5 w-3.5 shrink-0" />
                    {t(
                      msg`Team remote: every member of this organization sees it and its folders.`,
                    )}
                  </p>
                </Show>
              </div>
            </Show>
            <div class="space-y-2">
              <Label for="byos-name" class="text-xs font-semibold">
                {t(msg`Display name`)}
              </Label>
              <TextField
                id="byos-name"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                placeholder={t(msg`My backup drive`)}
                data-testid="byos-remote-name"
              />
            </div>

            <Show when={kind() === "s3"}>
              <div class="grid grid-cols-2 gap-2">
                <div class="space-y-2">
                  <Label for="byos-bucket" class="text-xs font-semibold">
                    {t(msg`Bucket`)}
                  </Label>
                  <TextField
                    id="byos-bucket"
                    value={bucket()}
                    onInput={(e) => setBucket(e.currentTarget.value)}
                    data-testid="byos-bucket"
                  />
                </div>
                <div class="space-y-2">
                  <Label for="byos-region" class="text-xs font-semibold">
                    {t(msg`Region`)}
                  </Label>
                  <TextField
                    id="byos-region"
                    value={region()}
                    onInput={(e) => setRegion(e.currentTarget.value)}
                    placeholder="us-east-1"
                  />
                </div>
              </div>
              <div class="space-y-2">
                <Label for="byos-endpoint" class="text-xs font-semibold">
                  {t(msg`Endpoint (R2/B2/MinIO — leave empty for AWS)`)}
                </Label>
                <TextField
                  id="byos-endpoint"
                  value={endpoint()}
                  onInput={(e) => setEndpoint(e.currentTarget.value)}
                  autocapitalize="none"
                  spellcheck={false}
                />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div class="space-y-2">
                  <Label for="byos-access" class="text-xs font-semibold">
                    {t(msg`Access key`)}
                  </Label>
                  <TextField
                    id="byos-access"
                    value={accessKey()}
                    onInput={(e) => setAccessKey(e.currentTarget.value)}
                    autocomplete="off"
                    autocapitalize="none"
                    spellcheck={false}
                  />
                </div>
                <div class="space-y-2">
                  <Label for="byos-secret" class="text-xs font-semibold">
                    {t(msg`Secret key`)}
                  </Label>
                  <TextField
                    id="byos-secret"
                    type="password"
                    value={secretKey()}
                    onInput={(e) => setSecretKey(e.currentTarget.value)}
                    autocomplete="off"
                  />
                </div>
              </div>
              <label class="flex items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={forcePathStyle()} onCheckedChange={setForcePathStyle} />
                {t(msg`Path-style URLs (required for MinIO)`)}
              </label>
            </Show>

            <Show when={kind() === "webdav"}>
              <div class="space-y-2">
                <Label for="byos-baseurl" class="text-xs font-semibold">
                  {t(msg`Server URL`)}
                </Label>
                <TextField
                  id="byos-baseurl"
                  value={baseUrl()}
                  onInput={(e) => setBaseUrl(e.currentTarget.value)}
                  placeholder="https://cloud.example.com/remote.php/dav/files/me/"
                  autocapitalize="none"
                  spellcheck={false}
                  data-testid="byos-baseurl"
                />
              </div>
              <div class="grid grid-cols-2 gap-2">
                <div class="space-y-2">
                  <Label for="byos-user" class="text-xs font-semibold">
                    {t(msg`Username`)}
                  </Label>
                  <TextField
                    id="byos-user"
                    value={username()}
                    onInput={(e) => setUsername(e.currentTarget.value)}
                    autocomplete="off"
                    autocapitalize="none"
                  />
                </div>
                <div class="space-y-2">
                  <Label for="byos-pass" class="text-xs font-semibold">
                    {t(msg`Password`)}
                  </Label>
                  <TextField
                    id="byos-pass"
                    type="password"
                    value={password()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                    autocomplete="off"
                  />
                </div>
              </div>
            </Show>

            <Show when={kind() === "drive"}>
              <p class="rounded-lg border border-dashed bg-muted/30 p-3 text-xs text-muted-foreground">
                {t(
                  msg`Google Drive uses OAuth and is connected manually for now — see the BYOS docs. The remote is saved as a stub you can finish linking later.`,
                )}
              </p>
            </Show>

            <label class="flex items-center gap-2 text-xs">
              <Switch checked={encrypted()} onCheckedChange={setEncrypted} />
              <Lock class="h-3.5 w-3.5 text-muted-foreground" />
              {t(msg`End-to-end encrypt this remote (only ciphertext leaves this device)`)}
            </label>

            <Button
              onClick={() => void connect()}
              disabled={!canConnect()}
              data-testid="byos-connect-submit"
              class="w-full gap-2"
            >
              <Show when={connecting()} fallback={<Plus class="h-4 w-4" />}>
                <Loader2 class="h-4 w-4 animate-spin" />
              </Show>
              {t(msg`Connect`)}
            </Button>
            <Show when={connectError()}>
              <p class="text-sm text-destructive" data-testid="byos-connect-error">
                {connectError()}
              </p>
            </Show>
          </CardContent>
        </Card>

        {/* Connected remotes */}
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <Cloud class="h-4 w-4 text-primary" />
              {t(msg`Connected remotes`)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Show when={remotes.isError}>
              <QueryErrorState
                testId="byos-remotes-error"
                compact
                onRetry={() => void remotes.refetch()}
              />
            </Show>
            <Show when={!remotes.isError}>
              <Show
                when={!remotes.isLoading}
                fallback={<Skeleton class="h-24 w-full rounded-xl" />}
              >
                <Show
                  when={(remotes.data?.length ?? 0) > 0}
                  fallback={
                    <div
                      class="flex flex-col items-center justify-center rounded-xl border border-dashed p-8 text-center"
                      data-testid="byos-remotes-empty"
                    >
                      <CloudOff class="mb-3 h-8 w-8 text-muted-foreground opacity-20" />
                      <p class="text-sm text-muted-foreground">
                        {t(msg`No remotes connected yet.`)}
                      </p>
                    </div>
                  }
                >
                  <div class="grid gap-2">
                    <For each={remotes.data ?? []}>
                      {(remote) => (
                        <button
                          type="button"
                          data-testid="byos-remote"
                          onClick={() => {
                            setSelectedRemote(remote.id);
                            setSelectedFolder(null);
                          }}
                          class="flex items-center gap-3 rounded-lg border p-3 text-left transition-colors hover:bg-accent/5"
                          classList={{
                            "border-primary bg-primary/5": selectedRemote() === remote.id,
                          }}
                        >
                          <div class="flex h-9 w-9 items-center justify-center rounded-full bg-muted">
                            <HardDrive class="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div class="min-w-0 flex-1">
                            <p class="truncate font-semibold" data-testid="byos-remote-title">
                              {remote.name}
                            </p>
                            <p class="truncate text-xs text-muted-foreground">
                              {remote.endpoint_label ?? remote.kind}
                            </p>
                          </div>
                          <Show when={remote.encrypted}>
                            <Lock class="h-3.5 w-3.5 text-primary" />
                          </Show>
                          {/* teamBadgeLabel: null = personal (no badge); "" = org
                            unknown in the user's list → generic "Team" label */}
                          <Show when={remote.organization_id}>
                            <Badge variant="secondary" class="gap-1" data-testid="byos-remote-team">
                              <Users class="h-3 w-3" />
                              {teamBadgeLabel(
                                remote.organization_id,
                                organizations.isSuccess ? organizations.data : undefined,
                              ) || t(msg`Team`)}
                            </Badge>
                          </Show>
                          <Badge variant="outline" class="uppercase">
                            {remote.kind}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t(msg`Disconnect`)}
                            data-testid="byos-remote-disconnect"
                            onClick={(e) => {
                              e.stopPropagation();
                              void disconnect(remote.id);
                            }}
                          >
                            <Trash2 class="h-4 w-4 text-destructive" />
                          </Button>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </Show>
          </CardContent>
        </Card>
      </div>

      {/* Folders of the selected remote */}
      <Show when={selectedRemote()}>
        <Card>
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <FolderSync class="h-4 w-4 text-primary" />
              {t(msg`Synced folders`)}
            </CardTitle>
          </CardHeader>
          <CardContent class="space-y-4">
            <div class="flex flex-wrap items-end gap-2">
              <div class="min-w-[10rem] flex-1 space-y-2">
                <Label for="byos-folder-name" class="text-xs font-semibold">
                  {t(msg`Folder name`)}
                </Label>
                <TextField
                  id="byos-folder-name"
                  value={folderName()}
                  onInput={(e) => setFolderName(e.currentTarget.value)}
                  placeholder={t(msg`Documents`)}
                  data-testid="byos-folder-name"
                />
              </div>
              <div class="min-w-[10rem] flex-1 space-y-2">
                <Label for="byos-folder-prefix" class="text-xs font-semibold">
                  {t(msg`Remote prefix (optional)`)}
                </Label>
                <TextField
                  id="byos-folder-prefix"
                  value={remotePrefix()}
                  onInput={(e) => setRemotePrefix(e.currentTarget.value)}
                  placeholder="docs/"
                  autocapitalize="none"
                  spellcheck={false}
                />
              </div>
              <Button
                onClick={() => void addFolder()}
                disabled={!folderName().trim() || addingFolder()}
                data-testid="byos-folder-add"
                class="gap-2"
              >
                <Show when={addingFolder()} fallback={<FolderPlus class="h-4 w-4" />}>
                  <Loader2 class="h-4 w-4 animate-spin" />
                </Show>
                {t(msg`Add folder`)}
              </Button>
            </div>

            <Show when={folders.isError}>
              <QueryErrorState
                testId="byos-folders-error"
                compact
                onRetry={() => void folders.refetch()}
              />
            </Show>
            <Show when={!folders.isError}>
              <Show
                when={!folders.isLoading}
                fallback={<Skeleton class="h-16 w-full rounded-xl" />}
              >
                <Show
                  when={(folders.data?.length ?? 0) > 0}
                  fallback={
                    <p
                      class="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground"
                      data-testid="byos-folders-empty"
                    >
                      {t(msg`No folders on this remote yet.`)}
                    </p>
                  }
                >
                  <div class="flex flex-wrap gap-2">
                    <For each={folders.data ?? []}>
                      {(folder) => (
                        <button
                          type="button"
                          data-testid="byos-folder"
                          onClick={() => setSelectedFolder(folder.id)}
                          class="rounded-lg border px-3 py-2 text-sm transition-colors hover:bg-accent/5"
                          classList={{
                            "border-primary bg-primary/5": selectedFolder() === folder.id,
                          }}
                        >
                          {folder.name}
                          <Show when={folder.remote_prefix}>
                            <span class="ml-2 text-xs text-muted-foreground">
                              {folder.remote_prefix}
                            </span>
                          </Show>
                        </button>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </Show>
          </CardContent>
        </Card>

        {/* Team E2E key distribution (kit e2e-crypto) — only for org remotes */}
        <Show
          when={(() => {
            const remote = (remotes.isSuccess ? (remotes.data ?? []) : []).find(
              (r) => r.id === selectedRemote(),
            );
            return remote?.organization_id ? remote : null;
          })()}
        >
          {(teamRemote) => <TeamKeySection remote={teamRemote()} />}
        </Show>
      </Show>

      {/* Status + conflicts + versions of the selected folder */}
      <Show when={selectedFolder()}>
        {/* Sync status */}
        <Card data-testid="byos-status">
          <CardHeader>
            <CardTitle class="flex items-center justify-between text-base">
              <span class="flex items-center gap-2">
                <FolderSync class="h-4 w-4 text-primary" />
                {t(msg`Sync status`)}
              </span>
              {(() => {
                const b = healthBadge();
                return (
                  <Badge variant={b.variant} class="gap-1" data-testid="byos-status-health">
                    <b.icon class="h-3 w-3" />
                    {b.label}
                  </Badge>
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Show
              when={status().last}
              fallback={
                <p class="text-sm text-muted-foreground" data-testid="byos-status-empty">
                  {t(msg`This folder has not been synced from any device yet.`)}
                </p>
              }
            >
              {(last) => (
                <div class="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
                  <Stat label={t(msg`Uploaded`)} value={last().uploaded} />
                  <Stat label={t(msg`Downloaded`)} value={last().downloaded} />
                  <Stat label={t(msg`Conflicts`)} value={partitioned().conflicts.length} />
                  <Stat label={t(msg`Files moved`)} value={jobFileCount(last())} />
                  <div class="col-span-2 sm:col-span-4">
                    <p class="text-xs text-muted-foreground">
                      {t(msg`Last activity by`)} <b>{last().device}</b> ·{" "}
                      {new Date(last().created_at).toLocaleString()}
                    </p>
                    <Show when={last().error}>
                      <p class="mt-1 text-xs text-destructive">{last().error}</p>
                    </Show>
                  </div>
                </div>
              )}
            </Show>
          </CardContent>
        </Card>

        {/* Conflicts */}
        <Show when={partitioned().conflicts.length > 0}>
          <Card data-testid="byos-conflicts">
            <CardHeader>
              <CardTitle class="flex items-center gap-2 text-base text-destructive">
                <AlertTriangle class="h-4 w-4" />
                {t(msg`Conflicts`)}
              </CardTitle>
            </CardHeader>
            <CardContent class="space-y-3">
              <p class="text-xs text-muted-foreground">
                {t(
                  msg`Two devices changed the same file. Both versions are kept — pick which one to keep at its original path.`,
                )}
              </p>
              <For each={partitioned().conflicts}>
                {(conflict) => {
                  const sides = conflictSides(conflict);
                  return (
                    <div class="rounded-lg border p-3" data-testid="byos-conflict">
                      <p class="font-mono text-sm font-semibold" data-testid="byos-conflict-path">
                        {sides.targetPath}
                      </p>
                      <p class="text-xs text-muted-foreground">
                        {t(msg`Losing copy from`)} <b>{sides.device ?? t(msg`another device`)}</b> ·{" "}
                        {formatBytes(conflict.size)} · {shortHash(conflict.content_hash)}
                      </p>
                      <div class="mt-2 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyVersion() === conflict.id}
                          data-testid="byos-conflict-keep-current"
                          onClick={() => void resolve(conflict.id, true)}
                        >
                          {t(msg`Keep current`)}
                        </Button>
                        <Button
                          size="sm"
                          disabled={busyVersion() === conflict.id}
                          data-testid="byos-conflict-use-this"
                          onClick={() => void resolve(conflict.id, false)}
                        >
                          <Show when={busyVersion() === conflict.id}>
                            <Loader2 class="mr-1 h-3 w-3 animate-spin" />
                          </Show>
                          {t(msg`Use this version`)}
                        </Button>
                      </div>
                    </div>
                  );
                }}
              </For>
            </CardContent>
          </Card>
        </Show>

        {/* Version history */}
        <Card data-testid="byos-versions">
          <CardHeader>
            <CardTitle class="flex items-center gap-2 text-base">
              <History class="h-4 w-4 text-primary" />
              {t(msg`Version history`)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Show when={versions.isError}>
              <QueryErrorState
                testId="byos-versions-error"
                compact
                onRetry={() => void versions.refetch()}
              />
            </Show>
            <Show when={!versions.isError}>
              <Show
                when={!versions.isLoading}
                fallback={<Skeleton class="h-20 w-full rounded-xl" />}
              >
                <Show
                  when={partitioned().history.length > 0}
                  fallback={
                    <p
                      class="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground"
                      data-testid="byos-versions-empty"
                    >
                      {t(msg`No versions recorded yet — they appear as you sync.`)}
                    </p>
                  }
                >
                  <div class="grid gap-2">
                    <For each={partitioned().history}>
                      {(version) => (
                        <div
                          class="flex items-center gap-3 rounded-lg border p-3"
                          data-testid="byos-version"
                        >
                          <div class="min-w-0 flex-1">
                            <p class="truncate font-mono text-sm" data-testid="byos-version-path">
                              {version.path}
                            </p>
                            <p class="text-xs text-muted-foreground">
                              {formatBytes(version.size)} · {shortHash(version.content_hash)}
                              <Show when={version.device}> · {version.device}</Show> ·{" "}
                              {new Date(version.created_at).toLocaleString()}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            class="gap-1"
                            disabled={busyVersion() === version.id}
                            data-testid="byos-version-restore"
                            onClick={() => void restore(version.id)}
                          >
                            <Show
                              when={busyVersion() === version.id}
                              fallback={<RotateCcw class="h-3 w-3" />}
                            >
                              <Loader2 class="h-3 w-3 animate-spin" />
                            </Show>
                            {t(msg`Restore`)}
                          </Button>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>
              </Show>
            </Show>
          </CardContent>
        </Card>
      </Show>
    </div>
  );
}

function Stat(props: { label: string; value: number }) {
  return (
    <div class="rounded-lg bg-muted/40 p-2">
      <p class="text-lg font-bold tabular-nums">{props.value}</p>
      <p class="text-xs text-muted-foreground">{props.label}</p>
    </div>
  );
}
